import {
  type CSSProperties,
  type DragEvent,
  Fragment,
  type MouseEvent,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  CaretDownIcon,
  CaretUpDownIcon,
  CaretUpIcon,
} from "@phosphor-icons/react";
import { Flex, Reset, Table, Text, VisuallyHidden } from "@radix-ui/themes";
import {
  flexRender,
  type Header,
  type Row,
  type RowData,
  type SortingState,
  useTable,
} from "@tanstack/react-table";

import { cn } from "~/v2/utils/cn";
import {
  type DropDirection,
  getPointerDropDirection,
} from "~/v3-poc/utils/dropDirection";
import {
  type DataTableColumns,
  type DataTableFeatures,
  dataTableFeatures,
} from "./features";
import "./dataTable.css";

export type {
  DataTableColumnMeta,
  DataTableColumns,
  DataTableFeatures,
} from "./features";

export type DataTableColumnReorderDirection = DropDirection;
export type DataTableDensity = "condensed" | "normal" | "expanded";

export interface DataTableColumnReorder {
  sourceColumnId: string;
  targetColumnId: string;
  direction: DataTableColumnReorderDirection;
}

const COLUMN_REORDER_DRAG_TYPE = "application/x-atd-datatable-column";

interface DataTableBaseProps<TData extends RowData> {
  columns: DataTableColumns<TData>;
  data: TData[];
  /** Radix Table variant. `surface` gives the table its own panel. */
  variant?: "surface" | "ghost";
  size?: "1" | "2" | "3";
  initialSorting?: SortingState;
  showIdleSortIndicator?: boolean;
  /** Table caption, visually hidden but announced by screen readers. */
  caption?: string;
  /** Shown in place of rows when `data` is empty. */
  emptyMessage?: string;
  /** Rich empty state shown in place of `emptyMessage`. */
  emptyContent?: ReactNode;
  /** Marks selected rows visually and with `aria-selected`. */
  isRowSelected?: (row: TData) => boolean;
  /** Marks the current/active row visually without selection semantics. */
  isRowActive?: (row: TData) => boolean;
  /** Stable row identity; required for `forceExpandedId`. */
  getRowId?: (row: TData) => string;
  /** Force a specific row (by `getRowId`) expanded, e.g. an inline editor. */
  forceExpandedId?: string | null;
  /**
   * Marks additional rows expanded. Combined with `forceExpandedId` so more
   * than one row can show `renderExpanded` at once.
   */
  isRowExpanded?: (row: TData) => boolean;
  /**
   * When set, `aria-expanded` is applied only to these rows. Omit to keep the
   * existing `renderExpanded` / `forceExpandedId` contract.
   */
  isRowExpandable?: (row: TData) => boolean;
  /** Content rendered in a full-width row beneath an expanded row. */
  renderExpanded?: (row: TData) => ReactNode;
  /**
   * Pin expanded content to the visible scroll viewport width (ATD pattern).
   * Requires the table to scroll via Radix Table.Root's ScrollArea.
   */
  clampExpandedToViewport?: boolean;
  /** Extra class on the Table.Root host (e.g. positions density). */
  className?: string;
  /**
   * Opt-in header drag-reorder. When omitted, headers are not draggable.
   * Columns with `meta.reorderable === false` stay pinned in place.
   */
  onColumnReorder?: (reorder: DataTableColumnReorder) => void;
  horizontalLines?: boolean;
  verticalLines?: boolean;
  density?: DataTableDensity;
  getRowGroupId?: (row: TData) => string;
  onViewportScroll?: (event: Event) => void;
  contentMinWidth?: string;
}

type DataTableRowActivationProps<TData extends RowData> =
  | {
      onRowActivate?: undefined;
      getRowAriaLabel?: never;
      isRowActivatable?: never;
    }
  | {
      /** Makes each data row mouse and keyboard activatable. */
      onRowActivate: (row: TData, rowElement: HTMLTableRowElement) => void;
      /** Meaningful accessible name for each activatable row. */
      getRowAriaLabel: (row: TData) => string;
      /** Optionally excludes individual rows from the activation contract. */
      isRowActivatable?: (row: TData) => boolean;
    };

export type DataTableProps<TData extends RowData> = DataTableBaseProps<TData> &
  DataTableRowActivationProps<TData>;

const SORT_ICON = {
  asc: CaretUpIcon,
  desc: CaretDownIcon,
} as const;

const ARIA_SORT = {
  asc: "ascending",
  desc: "descending",
} as const;

const NESTED_INTERACTIVE_SELECTOR = [
  "a",
  "button",
  "input",
  "select",
  "textarea",
  "[contenteditable='true']",
  "[role='button']",
  "[role='checkbox']",
  "[role='link']",
].join(",");

const groupSortedRows = <TData extends RowData>(
  coreRows: Row<DataTableFeatures, TData>[],
  sortedRows: Row<DataTableFeatures, TData>[],
  getRowGroupId: (row: TData) => string,
): Row<DataTableFeatures, TData>[] => {
  const sourceGroups = new Map<string, Row<DataTableFeatures, TData>[]>();
  for (const row of coreRows) {
    const groupId = getRowGroupId(row.original);
    const group = sourceGroups.get(groupId);
    if (group) {
      group.push(row);
    } else {
      sourceGroups.set(groupId, [row]);
    }
  }

  const sortedRowIndex = new Map(
    sortedRows.map((row, index) => [row.id, index] as const),
  );
  return [...sourceGroups.values()]
    .sort(
      (left, right) =>
        (sortedRowIndex.get(left[0].id) ?? Number.MAX_SAFE_INTEGER) -
        (sortedRowIndex.get(right[0].id) ?? Number.MAX_SAFE_INTEGER),
    )
    .flat();
};

const isNestedInteractiveTarget = (
  target: EventTarget | null,
  row: HTMLElement,
): boolean =>
  target instanceof Element &&
  target !== row &&
  target.closest(NESTED_INTERACTIVE_SELECTOR) !== null;

const getColumnStickyId = <TData extends RowData>(
  column: DataTableColumns<TData>[number],
): string | undefined => {
  if (typeof column.id === "string" && column.id.length > 0) {
    return column.id;
  }
  if ("accessorKey" in column && column.accessorKey != null) {
    return String(column.accessorKey);
  }
  return undefined;
};

const getStickyEdgeColumnIds = <TData extends RowData>(
  columns: DataTableColumns<TData>,
): { start?: string; end?: string } => {
  const startIds: string[] = [];
  const endIds: string[] = [];

  for (const column of columns) {
    const columnId = getColumnStickyId(column);
    if (!columnId) {
      continue;
    }
    if (column.meta?.sticky === "start") {
      startIds.push(columnId);
    }
    if (column.meta?.sticky === "end") {
      endIds.push(columnId);
    }
  }

  return {
    start: startIds.at(-1),
    end: endIds[0],
  };
};

const getStickyStyle = (
  sticky: "start" | "end" | undefined,
  stickyOffset: string | undefined,
  zIndex: number,
  isEdge: boolean,
): CSSProperties | undefined => {
  if (!sticky) {
    return undefined;
  }

  const pinEdge = isEdge
    ? sticky === "end"
      ? "inset 1px 0 0 var(--gray-a5)"
      : "inset -1px 0 0 var(--gray-a5)"
    : undefined;

  return {
    position: "sticky",
    [sticky === "start" ? "left" : "right"]: stickyOffset ?? "0",
    zIndex,
    // Keep row-state background images visible over pinned cells.
    backgroundColor: "var(--color-panel-solid)",
    boxShadow: pinEdge
      ? `var(--table-row-box-shadow), ${pinEdge}`
      : "var(--table-row-box-shadow)",
  };
};

const isHeaderReorderable = <TData extends RowData>(
  header: Header<DataTableFeatures, TData, unknown>,
  columnReorderEnabled: boolean,
): boolean =>
  columnReorderEnabled && header.column.columnDef.meta?.reorderable !== false;

/** A header whose label is a button that cycles the column's sort direction. */
const SortableHeaderContent = <TData extends RowData>({
  header,
  reorderable,
  showIdleSortIndicator,
  suppressSortClickRef,
  onDragStart,
  onDragEnd,
}: {
  header: Header<DataTableFeatures, TData, unknown>;
  reorderable: boolean;
  showIdleSortIndicator: boolean;
  suppressSortClickRef: { current: boolean };
  onDragStart: (event: DragEvent<HTMLButtonElement>, columnId: string) => void;
  onDragEnd: () => void;
}) => {
  const direction = header.column.getIsSorted();
  const Icon = direction
    ? SORT_ICON[direction]
    : showIdleSortIndicator
      ? CaretUpDownIcon
      : null;

  return (
    // A header cell's click target fills the cell, so it carries none of a
    // Radix `Button`'s chrome. `Reset` is how Radix strips the native button
    // styles for a case like this — hand-rolling `all: unset` duplicates it.
    // `Reset` also drops the focus outline, so the ring is restored explicitly.
    <Reset>
      <button
        type="button"
        draggable={reorderable || undefined}
        className={cn(
          "block w-full focus-visible:shadow-[inset_0_0_0_2px_var(--focus-8)]",
          reorderable ? "cursor-grab active:cursor-grabbing" : "cursor-pointer",
        )}
        onClick={(event: MouseEvent<HTMLButtonElement>) => {
          if (suppressSortClickRef.current) {
            event.preventDefault();
            suppressSortClickRef.current = false;
            return;
          }
          header.column.getToggleSortingHandler()?.(event);
        }}
        onDragStart={
          reorderable
            ? (event) => onDragStart(event, header.column.id)
            : undefined
        }
        onDragEnd={reorderable ? onDragEnd : undefined}
      >
        <Flex
          align="center"
          gap="1"
          justify={header.column.columnDef.meta?.align ?? "start"}
        >
          {flexRender(header.column.columnDef.header, header.getContext())}
          {Icon ? (
            <Icon
              aria-hidden
              size={12}
              weight="bold"
              className={cn(
                !direction && showIdleSortIndicator && "opacity-40",
              )}
            />
          ) : null}
        </Flex>
      </button>
    </Reset>
  );
};

const DataTableHeaderCell = <TData extends RowData>({
  header,
  isStickyEdge,
  columnReorderEnabled,
  draggingColumnId,
  dropDirection,
  showIdleSortIndicator,
  suppressSortClickRef,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
}: {
  header: Header<DataTableFeatures, TData, unknown>;
  isStickyEdge: boolean;
  columnReorderEnabled: boolean;
  draggingColumnId: string | null;
  dropDirection: DropDirection | undefined;
  showIdleSortIndicator: boolean;
  suppressSortClickRef: { current: boolean };
  onDragStart: (event: DragEvent<HTMLElement>, columnId: string) => void;
  onDragEnd: () => void;
  onDragOver: (
    event: DragEvent<HTMLElement>,
    columnId: string,
    reorderable: boolean,
  ) => void;
  onDrop: (
    event: DragEvent<HTMLElement>,
    columnId: string,
    reorderable: boolean,
  ) => void;
}) => {
  const meta = header.column.columnDef.meta;
  const direction = header.column.getIsSorted();
  const canSort = header.column.getCanSort();
  const reorderable = isHeaderReorderable(header, columnReorderEnabled);
  const dragFromCell = reorderable && !canSort;

  return (
    <Table.ColumnHeaderCell
      justify={meta?.align ?? "start"}
      width={meta?.width}
      aria-sort={direction ? ARIA_SORT[direction] : undefined}
      data-sticky={meta?.sticky}
      data-sticky-edge={isStickyEdge ? meta?.sticky : undefined}
      data-drop-indicator={dropDirection}
      draggable={dragFromCell || undefined}
      className={cn(
        meta?.sticky && "data-table-cell-sticky",
        isStickyEdge && "data-table-cell-sticky-edge",
        meta?.headerClassName,
        dragFromCell && "cursor-grab active:cursor-grabbing",
        dropDirection === "before" &&
          "shadow-[inset_2px_0_0_0_var(--accent-9)]",
        dropDirection === "after" &&
          "shadow-[inset_-2px_0_0_0_var(--accent-9)]",
        draggingColumnId === header.column.id && "opacity-60",
      )}
      style={getStickyStyle(
        meta?.sticky,
        meta?.stickyOffset,
        meta?.sticky ? 4 : 3,
        isStickyEdge,
      )}
      onDragStart={
        dragFromCell
          ? (event) => onDragStart(event, header.column.id)
          : undefined
      }
      onDragEnd={dragFromCell ? onDragEnd : undefined}
      onDragOver={
        columnReorderEnabled
          ? (event) => onDragOver(event, header.column.id, reorderable)
          : undefined
      }
      onDrop={
        reorderable
          ? (event) => onDrop(event, header.column.id, true)
          : undefined
      }
    >
      {header.isPlaceholder ? null : canSort ? (
        <SortableHeaderContent
          header={header}
          reorderable={reorderable}
          showIdleSortIndicator={showIdleSortIndicator}
          suppressSortClickRef={suppressSortClickRef}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
        />
      ) : (
        flexRender(header.column.columnDef.header, header.getContext())
      )}
    </Table.ColumnHeaderCell>
  );
};

const DataTableBodyRow = <TData extends RowData>({
  row,
  columnCount,
  isRowSelected,
  isRowActive,
  getRowId,
  forceExpandedId,
  isRowExpanded,
  isRowExpandable,
  renderExpanded,
  clampExpandedToViewport,
  viewportWidth,
  onRowActivate,
  getRowAriaLabel,
  isRowActivatable,
  stickyEdgeColumnIds,
}: {
  row: Row<DataTableFeatures, TData>;
  columnCount: number;
  isRowSelected?: (row: TData) => boolean;
  isRowActive?: (row: TData) => boolean;
  getRowId?: (row: TData) => string;
  forceExpandedId?: string | null;
  isRowExpanded?: (row: TData) => boolean;
  isRowExpandable?: (row: TData) => boolean;
  renderExpanded?: (row: TData) => ReactNode;
  clampExpandedToViewport: boolean;
  viewportWidth: number | undefined;
  onRowActivate?: (row: TData, rowElement: HTMLTableRowElement) => void;
  getRowAriaLabel?: (row: TData) => string;
  isRowActivatable?: (row: TData) => boolean;
  stickyEdgeColumnIds: { start?: string; end?: string };
}) => {
  const isSelected = isRowSelected?.(row.original) ?? false;
  const isActive = isRowActive?.(row.original) ?? false;
  const canActivate =
    onRowActivate !== undefined && (isRowActivatable?.(row.original) ?? true);
  const rowKey = getRowId?.(row.original) ?? row.id;
  const isExpanded =
    Boolean(renderExpanded) &&
    ((forceExpandedId != null && forceExpandedId === rowKey) ||
      isRowExpanded?.(row.original) === true);
  const announceExpanded = isRowExpandable
    ? isRowExpandable(row.original)
    : Boolean(renderExpanded);

  return (
    <Fragment>
      <Table.Row
        align="center"
        className={cn(
          canActivate && "data-table-row-activatable",
          isActive && "data-table-row-active",
          isSelected && "data-table-row-selected",
        )}
        tabIndex={canActivate ? 0 : undefined}
        aria-label={canActivate ? getRowAriaLabel?.(row.original) : undefined}
        aria-selected={isSelected ? true : undefined}
        aria-expanded={announceExpanded ? isExpanded : undefined}
        onClick={
          onRowActivate && canActivate
            ? (event) => {
                if (
                  !isNestedInteractiveTarget(event.target, event.currentTarget)
                ) {
                  onRowActivate(row.original, event.currentTarget);
                }
              }
            : undefined
        }
        onKeyDown={
          onRowActivate && canActivate
            ? (event) => {
                if (
                  isNestedInteractiveTarget(
                    event.target,
                    event.currentTarget,
                  ) ||
                  (event.key !== "Enter" && event.key !== " ")
                ) {
                  return;
                }
                event.preventDefault();
                onRowActivate(row.original, event.currentTarget);
              }
            : undefined
        }
      >
        {row.getAllCells().map((cell) => {
          const meta = cell.column.columnDef.meta;
          const isStickyEdge =
            (meta?.sticky === "start" &&
              cell.column.id === stickyEdgeColumnIds.start) ||
            (meta?.sticky === "end" &&
              cell.column.id === stickyEdgeColumnIds.end);
          return (
            <Table.Cell
              key={cell.id}
              justify={meta?.align ?? "start"}
              data-sticky={meta?.sticky}
              data-sticky-edge={isStickyEdge ? meta?.sticky : undefined}
              className={cn(
                meta?.numeric && "atd-numeric",
                meta?.sticky && "data-table-cell-sticky",
                isStickyEdge && "data-table-cell-sticky-edge",
                meta?.className,
              )}
              style={getStickyStyle(
                meta?.sticky,
                meta?.stickyOffset,
                2,
                isStickyEdge,
              )}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </Table.Cell>
          );
        })}
      </Table.Row>
      {isExpanded ? (
        <Table.Row className="data-table-row-expanded">
          <Table.Cell colSpan={columnCount} p="0">
            {clampExpandedToViewport ? (
              <div
                className="data-table-expanded-viewport"
                style={{
                  position: "sticky",
                  left: 0,
                  width: viewportWidth,
                  maxWidth: "100%",
                }}
              >
                {renderExpanded?.(row.original)}
              </div>
            ) : (
              renderExpanded?.(row.original)
            )}
          </Table.Cell>
        </Table.Row>
      ) : null}
    </Fragment>
  );
};

export const DataTable = <TData extends RowData>({
  columns,
  data,
  variant = "ghost",
  size = "1",
  initialSorting = [],
  showIdleSortIndicator = false,
  caption,
  emptyMessage = "No rows to show.",
  emptyContent,
  isRowSelected,
  isRowActive,
  getRowId,
  forceExpandedId,
  isRowExpanded,
  isRowExpandable,
  renderExpanded,
  clampExpandedToViewport = false,
  className,
  horizontalLines = true,
  verticalLines = false,
  density = "normal",
  getRowGroupId,
  onRowActivate,
  getRowAriaLabel,
  isRowActivatable,
  onColumnReorder,
  onViewportScroll,
  contentMinWidth,
}: DataTableProps<TData>) => {
  const [sorting, setSorting] = useState<SortingState>(initialSorting);
  const tableRootRef = useRef<HTMLDivElement>(null);
  const [viewportWidth, setViewportWidth] = useState<number>();
  const [draggingColumnId, setDraggingColumnId] = useState<string | null>(null);
  const [dropIndicator, setDropIndicator] = useState<{
    columnId: string;
    direction: DropDirection;
  } | null>(null);
  const suppressSortClickRef = useRef(false);
  const columnReorderEnabled = onColumnReorder !== undefined;
  const stickyEdgeColumnIds = useMemo(
    () => getStickyEdgeColumnIds(columns),
    [columns],
  );

  const clearColumnDrag = () => {
    setDraggingColumnId(null);
    setDropIndicator(null);
  };

  const handleColumnDragStart = (
    event: DragEvent<HTMLElement>,
    columnId: string,
  ) => {
    event.dataTransfer.setData(COLUMN_REORDER_DRAG_TYPE, columnId);
    event.dataTransfer.setData("text/plain", columnId);
    event.dataTransfer.effectAllowed = "move";
    suppressSortClickRef.current = true;
    setDraggingColumnId(columnId);
    setDropIndicator(null);
  };

  const handleColumnDragEnd = () => {
    clearColumnDrag();
    window.setTimeout(() => {
      suppressSortClickRef.current = false;
    }, 0);
  };

  const handleColumnDragOver = (
    event: DragEvent<HTMLElement>,
    columnId: string,
    reorderable: boolean,
  ) => {
    if (!draggingColumnId) {
      return;
    }
    if (!reorderable || draggingColumnId === columnId) {
      setDropIndicator(null);
      return;
    }
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    const rect = event.currentTarget.getBoundingClientRect();
    const direction = getPointerDropDirection(
      "horizontal",
      event.clientX,
      event.clientY,
      rect,
    );
    setDropIndicator((current) =>
      current?.columnId === columnId && current.direction === direction
        ? current
        : { columnId, direction },
    );
  };

  const handleColumnDrop = (
    event: DragEvent<HTMLElement>,
    targetColumnId: string,
    reorderable: boolean,
  ) => {
    event.preventDefault();
    const sourceColumnId =
      event.dataTransfer.getData(COLUMN_REORDER_DRAG_TYPE) ||
      draggingColumnId ||
      event.dataTransfer.getData("text/plain");
    const direction =
      dropIndicator?.columnId === targetColumnId
        ? dropIndicator.direction
        : getPointerDropDirection(
            "horizontal",
            event.clientX,
            event.clientY,
            event.currentTarget.getBoundingClientRect(),
          );
    clearColumnDrag();
    if (
      !onColumnReorder ||
      !reorderable ||
      !sourceColumnId ||
      sourceColumnId === targetColumnId
    ) {
      return;
    }
    onColumnReorder({
      sourceColumnId,
      targetColumnId,
      direction,
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: columns/data length retrigger viewport measure when the table size changes; the effect writes rather than reads those values.
  useLayoutEffect(() => {
    if (!clampExpandedToViewport || !renderExpanded) {
      return;
    }
    const root = tableRootRef.current;
    if (!root) {
      return;
    }
    // Radix Table.Root wraps <table> in ScrollArea — measure that viewport.
    const viewport =
      (root.querySelector(".rt-ScrollAreaViewport") as HTMLElement | null) ??
      root;
    const update = () => setViewportWidth(viewport.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [clampExpandedToViewport, renderExpanded, columns.length, data.length]);

  useEffect(() => {
    if (!onViewportScroll) {
      return;
    }
    const root = tableRootRef.current;
    if (!root) {
      return;
    }
    const viewport =
      (root.querySelector(".rt-ScrollAreaViewport") as HTMLElement | null) ??
      root;
    viewport.addEventListener("scroll", onViewportScroll);
    return () => viewport.removeEventListener("scroll", onViewportScroll);
  }, [onViewportScroll]);

  const table = useTable({
    features: dataTableFeatures,
    columns,
    data,
    state: { sorting },
    onSortingChange: setSorting,
    ...(getRowId ? { getRowId: (row: TData) => getRowId(row) } : {}),
  });

  const sortedRows = table.getRowModel().rows;
  const rows = getRowGroupId
    ? groupSortedRows(table.getCoreRowModel().rows, sortedRows, getRowGroupId)
    : sortedRows;
  const columnCount = columns.length;

  return (
    <Table.Root
      ref={tableRootRef}
      variant={variant}
      size={size}
      className={cn(
        "data-table-root",
        !horizontalLines && "data-table--no-hlines",
        verticalLines && "data-table--vlines",
        density === "condensed" && "data-table--condensed",
        density === "expanded" && "data-table--expanded",
        className,
      )}
      style={
        contentMinWidth
          ? { ["--data-table-min-width" as string]: contentMinWidth }
          : undefined
      }
    >
      {caption ? (
        <VisuallyHidden asChild>
          <caption>{caption}</caption>
        </VisuallyHidden>
      ) : null}

      <Table.Header>
        {table.getHeaderGroups().map((headerGroup) => (
          <Table.Row key={headerGroup.id} align="center">
            {headerGroup.headers.map((header) => (
              <DataTableHeaderCell
                key={header.id}
                header={header}
                isStickyEdge={
                  (header.column.columnDef.meta?.sticky === "start" &&
                    header.column.id === stickyEdgeColumnIds.start) ||
                  (header.column.columnDef.meta?.sticky === "end" &&
                    header.column.id === stickyEdgeColumnIds.end)
                }
                columnReorderEnabled={columnReorderEnabled}
                draggingColumnId={draggingColumnId}
                dropDirection={
                  dropIndicator?.columnId === header.column.id
                    ? dropIndicator.direction
                    : undefined
                }
                showIdleSortIndicator={showIdleSortIndicator}
                suppressSortClickRef={suppressSortClickRef}
                onDragStart={handleColumnDragStart}
                onDragEnd={handleColumnDragEnd}
                onDragOver={handleColumnDragOver}
                onDrop={handleColumnDrop}
              />
            ))}
          </Table.Row>
        ))}
      </Table.Header>

      <Table.Body>
        {rows.map((row) => (
          <DataTableBodyRow
            key={row.id}
            row={row}
            columnCount={columnCount}
            isRowSelected={isRowSelected}
            isRowActive={isRowActive}
            getRowId={getRowId}
            forceExpandedId={forceExpandedId}
            isRowExpanded={isRowExpanded}
            isRowExpandable={isRowExpandable}
            renderExpanded={renderExpanded}
            clampExpandedToViewport={clampExpandedToViewport}
            viewportWidth={viewportWidth}
            onRowActivate={onRowActivate}
            getRowAriaLabel={getRowAriaLabel}
            isRowActivatable={isRowActivatable}
            stickyEdgeColumnIds={stickyEdgeColumnIds}
          />
        ))}

        {rows.length === 0 ? (
          <Table.Row align="center">
            <Table.Cell colSpan={columnCount}>
              {emptyContent ?? (
                <Text size="2" color="gray">
                  {emptyMessage}
                </Text>
              )}
            </Table.Cell>
          </Table.Row>
        ) : null}
      </Table.Body>
    </Table.Root>
  );
};
